import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useBilling } from '../../contexts/BillingContext';

const SHOPIFY_ACCESS_GRACE_KEY = 'makersstuff_shopify_access_grace';

function readShopifyAccessGrace() {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(SHOPIFY_ACCESS_GRACE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      window.sessionStorage.removeItem(SHOPIFY_ACCESS_GRACE_KEY);
      return null;
    }

    const expiresAt = Number(parsed.expiresAt ?? 0);
    if (!expiresAt || expiresAt <= Date.now()) {
      window.sessionStorage.removeItem(SHOPIFY_ACCESS_GRACE_KEY);
      return null;
    }

    return {
      teamId: typeof parsed.teamId === 'string' ? parsed.teamId : '',
      shop: typeof parsed.shop === 'string' ? parsed.shop : '',
      expiresAt,
    };
  } catch (_) {
    window.sessionStorage.removeItem(SHOPIFY_ACCESS_GRACE_KEY);
    return null;
  }
}

function clearShopifyAccessGrace() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(SHOPIFY_ACCESS_GRACE_KEY);
}

const GateMessage = ({ title, message }) => (
  <div className="min-h-screen flex items-center justify-center bg-muted px-4">
    <div className="max-w-md w-full rounded-lg border border-border bg-card p-6 text-center shadow-elevation-2">
      <h1 className="text-lg font-semibold text-foreground">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-5 inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
      >
        Try Again
      </button>
    </div>
  </div>
);

// ProtectedRoute — gate order:
//   1. Auth loading → spinner
//   2. No user → /login
//   3. No team → /onboarding  (incomplete signup)
//   4. Shopify-connected but invalid Shopify billing → /billing
//   5. No active subscription → /pricing
//   5. All clear → render children
//
// Post-checkout flow: Stripe redirects to the public homepage (/)
// so there's no race condition with the webhook. By the time the
// user logs back in, billing_accounts.status will be 'active'.
const ProtectedRoute = ({ children }) => {
  const { user, loading, teamId, connectionError } = useAuth();
  const {
    hasActivePlan,
    loading: billingLoading,
    entitlements,
    needsShopifyBillingAction,
    refetch,
    error: billingError,
  } = useBilling();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const retryRef = useRef(false);

  const billing = entitlements?.billing || {};
  const shopParam = (searchParams.get('shop') ?? '').trim().toLowerCase();
  const hasShopifyUrlContext =
    (searchParams.get('shopify') ?? '').trim().toLowerCase() === 'connected'
    || Boolean(shopParam);
  const hasShopifyBillingContext =
    billing?.is_shopify_connected === true
    || Boolean(billing?.shop_domain)
    || billing?.provider === 'shopify';
  const hasShopifyContext = hasShopifyUrlContext || hasShopifyBillingContext;
  const entitlementsMatchActiveTeam = !entitlements?.team_id || entitlements.team_id === teamId;
  const shopifyAccessGrace = readShopifyAccessGrace();
  const hasShopifyAccessGrace = Boolean(
    shopifyAccessGrace && (
      (shopifyAccessGrace.teamId && shopifyAccessGrace.teamId === teamId)
      || (shopParam && shopifyAccessGrace.shop === shopParam)
    )
  );
  const normalizedBillingStatus = String(billing?.status ?? '').toLowerCase();
  const hasExplicitShopifyBillingFailure =
    billing?.provider === 'shopify'
    && ['canceled', 'past_due', 'paused', 'expired'].includes(normalizedBillingStatus);
  const hasUncertainShopifyBilling =
    hasShopifyContext
    && !hasActivePlan
    && !hasExplicitShopifyBillingFailure
    && (billing?.provider == null || billing?.provider === 'shopify')
    && ['none', 'incomplete', 'trialing', ''].includes(normalizedBillingStatus);
  const shouldDeferShopifyBillingLock =
    hasUncertainShopifyBilling
    && (hasShopifyUrlContext || hasShopifyAccessGrace);

  useEffect(() => {
    if (!hasShopifyAccessGrace) return;

    if (hasActivePlan || hasExplicitShopifyBillingFailure) {
      clearShopifyAccessGrace();
    }
  }, [hasShopifyAccessGrace, hasActivePlan, hasExplicitShopifyBillingFailure]);

  useEffect(() => {
    if (!shouldDeferShopifyBillingLock) {
      retryRef.current = false;
      return;
    }

    if (retryRef.current) return;
    retryRef.current = true;
    refetch();
  }, [shouldDeferShopifyBillingLock, refetch]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Getting your Stuff...</p>
        </div>
      </div>
    );
  }

  if (connectionError && !user) {
    return <GateMessage title="Connection Timed Out" message={connectionError} />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (connectionError && !teamId) {
    return <GateMessage title="Workspace Timed Out" message={connectionError} />;
  }

  if (!teamId) {
    return <Navigate to="/onboarding" replace />;
  }

  // Billing data can still be resolving on first protected-route render.
  // Wait until entitlements are present (or an explicit error exists)
  // before evaluating the subscription gate.
  const billingResolved = (!!entitlements && entitlementsMatchActiveTeam) || !!billingError;
  if (!billingResolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Getting your Stuff...</p>
        </div>
      </div>
    );
  }

  // Subscription gate for manual-enrollment (non-Shopify) teams.
  // Wait for entitlements to load so we know if this is a Shopify team.
  // If the fetch errors out, treat as "no plan" → redirect to /pricing.
  if (billingLoading && !billingError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Getting your Stuff...</p>
        </div>
      </div>
    );
  }

  if (billingError && !entitlements) {
    return <GateMessage title="Billing Check Timed Out" message={billingError} />;
  }

  if (
    (needsShopifyBillingAction || (hasShopifyContext && !hasActivePlan))
    && !shouldDeferShopifyBillingLock
    && location.pathname !== '/billing'
  ) {
    return <Navigate to="/billing" replace />;
  }

  if (!hasShopifyContext && !needsShopifyBillingAction && !hasActivePlan) {
    return <Navigate to="/pricing" replace />;
  }

  // Key on teamId so switching teams remounts all children (fresh data + subscriptions)
  return <React.Fragment key={teamId}>{children}</React.Fragment>;
};

export default ProtectedRoute;